import { FileUpIcon } from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { createFileRoute, useNavigate } from '@tanstack/react-router'

import { StateSwitcher } from '@/components/atoms/state-switcher'
import { EmptyState } from '@/components/molecules/empty-state'
import PageHeader from '@/components/molecules/page-header'
import { DataTableSkeleton } from '@/components/organisms/data-table'
import DocumentsTable from '@/components/organisms/documents-table'
import { Button } from '@/components/ui/button'
import { useSubscription } from '@/contexts/subscription-context'
import { useIndividuals } from '@/hooks/useIndividuals'

export const Route = createFileRoute('/_authenticated/documents/')({
  component: DocumentsListPage,
})

function DocumentsListPage() {
  const { individualQuery, selectedIndividual } = useIndividuals()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isReadOnly } = useSubscription()

  const documents = selectedIndividual?.documents ?? []
  const nonDrafts = documents.filter((d) => d.status !== 'draft')

  // Zero documents (drafts or done) → redirect to /documents/upload as the empty-state.
  const isLoading = individualQuery.isLoading
  const hasAny = documents.length > 0

  useEffect(() => {
    // The empty state normally sends people straight to upload. Don't, while
    // uploading is locked — it would drop them on a page they can't use.
    if (!isLoading && !hasAny && !isReadOnly) {
      navigate({ to: '/documents/upload' })
    }
  }, [isLoading, hasAny, isReadOnly, navigate])

  const goToUpload = () => navigate({ to: '/documents/upload' })

  return (
    <div className="space-y-8 z-10">
      <PageHeader
        body={t('documentsPage.header.description')}
        heading={t('documentsPage.header.title')}
      >
        <Button disabled={isReadOnly} onClick={goToUpload}>
          <FileUpIcon />
          {t('documentsPage.header.uploadDocumentButton')}
        </Button>
      </PageHeader>
      <StateSwitcher
        emptyState={
          <EmptyState
            body={t('documentsPage.emptyState.description')}
            ctaText={t('documentsPage.emptyState.cta')}
            ctaType="upload-file"
            heading={t('documentsPage.emptyState.title')}
            onClickCta={isReadOnly ? undefined : goToUpload}
          />
        }
        isEmpty={nonDrafts.length === 0}
        isLoading={isLoading}
        loadingState={<DataTableSkeleton />}
      >
        <DocumentsTable documents={nonDrafts} />
      </StateSwitcher>
    </div>
  )
}
